import * as React from "react";
import {useEffect} from "react";
import {AccountType, IBenchmarkReturn, IPerformanceTreeNode, IPlanTreeNode,} from "../../model/Portfolio.model";
import {useDispatch, useSelector} from "react-redux";
import {hasResearchAccess} from "../../../mainReducerMapSelectors";
import {
    buildPerformanceTreeByDate,
    getClassNameForManagementType,
    PerformanceType,
    PLAN_TREE_THEME
} from "./portfolioUtil";
import {Action, IdState} from "@table-library/react-table-library/types/common";
import {CellTree, Tree, useTree} from "@table-library/react-table-library/tree";
import {Link} from "react-router-dom";
import {Body, Cell, Header, HeaderCell, HeaderRow, Row, Table} from "@table-library/react-table-library/table";
import {ISelectValue} from "../common/Select.component";
import {performanceExpandedRows, performanceSelectedProduct} from "./PortfolioMapSelectors";
import {setPerformanceSelectedProductAction, updatePerformanceExpandedRowsAction} from "./PortfolioActions";
import {scrollToElement} from "../../utils/browserUtil";

export interface IPerformanceTreeTableProps {
    selectedDate: ISelectValue;
    datedPlanNodes: IPlanTreeNode[];
    performanceType: PerformanceType;
    benchmarkReturns: IBenchmarkReturn[];
    isDcPlan: boolean;
}
export const PerformanceTreeTable: React.FunctionComponent<IPerformanceTreeTableProps> = (props) => {
    const [nodes, setNodes] = React.useState<IPerformanceTreeNode[]>([]);
    const hasResearch = useSelector(state => hasResearchAccess(state));

    const expandedRows = useSelector(state => performanceExpandedRows(state));
    const selectedProduct = useSelector(state => performanceSelectedProduct(state));
    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedProduct) {
            setTimeout(() => {
                scrollToElement(`#link-${selectedProduct}`, 100, true);
            }, (10));
        }
    }, []);

    useEffect(() => {
        const date = (props.selectedDate.id as string);
        const benchmarks = props.performanceType === PerformanceType.ABSOLUTE
            ? props.benchmarkReturns
            : [];
        setNodes(buildPerformanceTreeByDate(props.datedPlanNodes, date, benchmarks));
    }, [props]);

    const data = {nodes};

    const onTreeChange = (action: Action, state: IdState) => {
        const expandedRowsToStore = state.ids.map((id) => id as number);
        dispatch(updatePerformanceExpandedRowsAction(expandedRowsToStore));
    };

    const tree: Tree<IPerformanceTreeNode> = useTree(
        data,
        {onChange: onTreeChange, state: {ids: expandedRows}},
        {treeIcon: {
                iconRight: (
                    <div className="far fa-chevron-right fa-chevron-tree"/>
                ),
                iconDown: (
                    <div className="far fa-chevron-down  fa-chevron-tree"/>
                ),
            }},
    );

    const renderName = (item: IPerformanceTreeNode) => {
        return hasResearch && item.backstopId
            ? <Link to={`/products/${item.backstopId}`}
                    onClick={() => dispatch(setPerformanceSelectedProductAction(item.id))} id={`link-${item.id}`}
            >
                {item.name}
            </Link>
            : item.name;
    };

    const renderValue = (node: IPerformanceTreeNode, value: number | null) => {
        const valueToDisplay = node.accountType === AccountType.ATOMIC || node.accountType === AccountType.BENCHMARK
            ? value === null ? "-" : value!.toFixed(2)
            : "";
        const className = getClassNameForManagementType(props.performanceType, valueToDisplay, node.managementType);

        return <div className={className}>{valueToDisplay}</div>;
    };

    const expandCollapse = () => {
        tree.fns.onToggleAll({isCarryForward: false, isPartialToAll: true});
    };

    const getNodeStyle = (item: IPerformanceTreeNode) => {
        return item.accountType === AccountType.COMPOSITE
            ? "portfolio__parent-node"
            : item.accountType === AccountType.BENCHMARK
                ? "portfolio__benchmark-node"
                : "";
    };

    const renderTree = () => {
        return (
            <Table data={data} tree={tree}
                   theme={PLAN_TREE_THEME("34% 11% 11% 11% 11% 11% 11%")}
                   layout={{custom: true, fixedHeader: true}}>
                {(nodes: IPerformanceTreeNode[]) => (
                    <>
                        <Header>
                            <HeaderRow>
                                <HeaderCell>Category</HeaderCell>
                                <HeaderCell>1 Quarter</HeaderCell>
                                <HeaderCell>Year To Date</HeaderCell>
                                <HeaderCell>1 Year</HeaderCell>
                                <HeaderCell>3 Years</HeaderCell>
                                <HeaderCell>5 Years</HeaderCell>
                                <HeaderCell>10 Years</HeaderCell>
                            </HeaderRow>
                        </Header>

                        <Body>
                            {nodes.map((item: IPerformanceTreeNode) => {
                                const performance
                                    = props.performanceType === PerformanceType.RELATIVE
                                        ? item.relativePerformance
                                        : item.absolutePerformance;
                                return (
                                    <Row key={item.id} item={item}
                                         className={getNodeStyle(item)}>
                                        <CellTree item={item}>
                                            {renderName(item)}
                                        </CellTree>
                                        <Cell>{renderValue(item, performance.quarter)}</Cell>
                                        <Cell>{renderValue(item, performance.ytd)}</Cell>
                                        <Cell>{renderValue(item, performance.year)}</Cell>
                                        <Cell>{renderValue(item, performance.threeYears)}</Cell>
                                        <Cell>{renderValue(item, performance.fiveYears)}</Cell>
                                        <Cell>{renderValue(item, performance.tenYears)}</Cell>
                                    </Row>
                                );
                            })}
                        </Body>
                    </>
                )}
            </Table>
        );
    };

    const getClassName = () => {
      return props.isDcPlan ? "portfolio__dc-header-container" : "";
    };

    return <div className="portfolio__tree-container" data-testid="portfolio__performance-tree">
        <div className={`portfolio__header-container ${getClassName()}`} data-testid="portfolio__header-container">
            <div className="portfolio__expand-collapse-container">
                <div className="portfolio__expand-collapse-text link-14 clickable performance" onClick={expandCollapse}>
                    Expand/Collapse All
                </div>
            </div>
        </div>
        <div className="portfolio__tree-table-container">
            {renderTree()}
        </div>
    </div>;
};